/**
 * 약관 동의 페이지 (V37)
 *
 * 최초 로그인 시 약관 동의를 강제하는 페이지입니다.
 * - 동의 버튼만 제공 (취소 시 로그아웃)
 * - 동의 완료 후 비밀번호 변경 페이지로 이동
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { CheckCircle, LogOut, Loader } from 'lucide-react';
import { auth, functions } from '../../firebase/config';

interface TermsData {
  version: string;
  content: string;
}

export function TermsPage() {
  const navigate = useNavigate();
  const [terms, setTerms] = useState<TermsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);

  // 약관 조회
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        if (!functions) {
          // 데모 모드
          setTerms({
            version: 'demo-v1',
            content: getDemoTermsContent(),
          });
          setLoading(false);
          return;
        }

        const getCurrentTerms = httpsCallable<void, TermsData>(functions, 'getCurrentTerms');
        const result = await getCurrentTerms();
        setTerms(result.data);
      } catch (err: any) {
        console.error('약관 조회 실패:', err);
        setError('약관 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, []);

  // 약관 동의 처리
  const handleAccept = async () => {
    if (!agreed || !terms) return;

    setSubmitting(true);
    setError('');

    try {
      if (!functions) {
        // 데모 모드
        navigate('/auth/change-password');
        return;
      }

      const acceptTerms = httpsCallable<{ version: string }, { success: boolean }>(
        functions,
        'acceptTerms'
      );
      await acceptTerms({ version: terms.version });

      // 비밀번호 변경 페이지로 이동
      navigate('/auth/change-password');
    } catch (err: any) {
      console.error('약관 동의 실패:', err);
      setError(err.message || '약관 동의 처리에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  // 거부 시 로그아웃
  const handleReject = async () => {
    if (auth) {
      await signOut(auth);
    }
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <Loader size={48} style={{ animation: 'spin 1s linear infinite' }} />
        <p>약관 정보를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* 헤더 */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>서비스 이용 약관</h1>
        </div>

        {/* 약관 내용 */}
        <div style={contentContainerStyle}>
          <pre style={contentStyle}>
            {terms?.content || '약관 내용을 불러올 수 없습니다.'}
          </pre>
        </div>

        {/* 동의 체크박스 */}
        <label style={checkboxLabelStyle}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            style={checkboxStyle}
          />
          <span>위 약관 내용을 모두 읽었으며, 이에 동의합니다.</span>
        </label>

        {/* 에러 메시지 */}
        {error && <p style={errorStyle}>{error}</p>}

        {/* 버튼 영역 */}
        <div style={buttonContainerStyle}>
          <button
            onClick={handleReject}
            style={rejectButtonStyle}
            disabled={submitting}
          >
            <LogOut size={18} />
            동의하지 않음 (로그아웃)
          </button>
          <button
            onClick={handleAccept}
            style={agreed ? acceptButtonStyle : disabledButtonStyle}
            disabled={!agreed || submitting}
          >
            {submitting ? (
              <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <CheckCircle size={18} />
            )}
            동의하고 계속하기
          </button>
        </div>

        {/* 버전 정보 */}
        <p style={versionStyle}>약관 버전: {terms?.version || '-'}</p>
      </div>
    </div>
  );
}

// 데모 모드용 약관 내용
function getDemoTermsContent(): string {
  return `[직원 정보 수집/이용 동의]

nGle-Works 시스템 이용을 위해 아래와 같이 개인정보를 수집/이용합니다.

1. 수집 항목
   - 기본정보: 이름(국/영문), 이메일, 전화번호, 주소
   - 인사정보: 생년월일, 주민등록번호/외국인등록번호(원문)
   - 근무정보: 고용이력, 조직, 직급, 직책, 입사일 등

2. 이용 목적
   - 인사관리: 채용, 배치, 근태, 휴가, 승진, 평가
   - 계약관리: 고용계약, 파견계약, 계약 연장/종료
   - 내부 커뮤니케이션: 조직도, 연락처, 공지사항

3. 보관 기간
   - 재직 기간 중 보관
   - 퇴직 후 관련 법령에 따라 일정 기간 보관 후 파기

4. 동의 거부 시
   - 본 약관에 동의하지 않으시면 시스템 이용이 제한됩니다.
   - 필수 정보 미제공 시 서비스 이용이 불가합니다.`;
}

// 스타일
const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f3f4f6',
  padding: '20px',
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  padding: '32px',
  maxWidth: '600px',
  width: '100%',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '24px',
};

const titleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: '700',
  margin: 0,
  color: '#1f2937',
};

const contentContainerStyle: React.CSSProperties = {
  maxHeight: '300px',
  overflowY: 'auto',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '20px',
  backgroundColor: '#f9fafb',
};

const contentStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  fontFamily: 'inherit',
  fontSize: '14px',
  lineHeight: '1.6',
  margin: 0,
  color: '#374151',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '20px',
  cursor: 'pointer',
  fontSize: '15px',
  color: '#1f2937',
};

const checkboxStyle: React.CSSProperties = {
  width: '18px',
  height: '18px',
  cursor: 'pointer',
};

const errorStyle: React.CSSProperties = {
  color: '#dc2626',
  fontSize: '14px',
  marginBottom: '16px',
};

const buttonContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
};

const rejectButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 20px',
  fontSize: '15px',
  fontWeight: '500',
  color: '#6b7280',
  backgroundColor: 'white',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  cursor: 'pointer',
};

const acceptButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 20px',
  fontSize: '15px',
  fontWeight: '600',
  color: 'white',
  backgroundColor: '#3b82f6',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const disabledButtonStyle: React.CSSProperties = {
  ...acceptButtonStyle,
  backgroundColor: '#9ca3af',
  cursor: 'not-allowed',
};

const versionStyle: React.CSSProperties = {
  textAlign: 'center',
  fontSize: '12px',
  color: '#9ca3af',
  marginTop: '16px',
};
